import { Fragment, useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { usePimsFacilitySummary } from '@/hooks/equip/useEquipPims'
import { useBreadcrumbs } from '@/views/equip/context/BreadcrumbContext'
import { motion, AnimatePresence } from 'framer-motion'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table'
import {
  AnimatedTableWrapper,
  AnimatedTableBody,
  AnimatedTableRow,
} from '@/components/tailwindadmin/components/animated-component/AnimatedTable'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/tailwindadmin/components/ui/select'
import { ChevronRight, Check, Download, ExternalLink, Search, ArrowUpDown, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { equipApi } from '@/services/equipApi'
import type { PIMsFacilitySummary, PIMsPatientDetail } from '@/lib/equip-types'
// equipApi still needed for loadFacilityPatients (user-triggered, imperative, not cached)
import { equipTable } from '@/views/equip/equip-table-styles'
import { SortableHeader } from '@/views/equip/components/SortableHeader'
import { SubGrid, SubGridContent } from '@/components/ui/sub-grid'

interface PIMsGridProps {
  providerNpi: string
  year: number
  quarter: number
  onPatientClick: (patient: { patientId: number; defaultTab: string }) => void
  embedded?: boolean
  /** @deprecated No longer used — PIMsGrid owns its loading state via React Query */
  isLoading?: boolean
}

interface FacilityWithPatients extends PIMsFacilitySummary {
  patients?: PIMsPatientDetail[]
  patientsLoading?: boolean
  patientsTotalCount?: number
  patientsPage?: number
}

export default function PIMsGrid({
  providerNpi,
  year,
  quarter,
  onPatientClick,
  embedded = false,
}: PIMsGridProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const [sorting, setSorting] = useState<SortingState>([])
  const [expanded, setExpanded] = useState({})
  const [globalFilter, setGlobalFilter] = useState('')
  const [showSearch, setShowSearch] = useState(false)

  // React Query: fetch facility summary — cache hit within 5 minutes produces no network request
  const { data: facilitySummaryData = [], isLoading } = usePimsFacilitySummary(
    providerNpi,
    year,
    quarter
  )

  // Local enriched state: starts from hook data, gets mutated by loadFacilityPatients.
  // A plain useState is needed here because loadFacilityPatients merges patient sub-rows
  // into the facility records (FacilityWithPatients extends PIMsFacilitySummary).
  // The facility row expand drill-down stays imperative (user-triggered, not cached).
  const [facilities, setFacilities] = useState<FacilityWithPatients[]>([])

  // Sync local facilities state when hook data changes (quarter change, cache invalidation)
  useEffect(() => {
    setFacilities(facilitySummaryData)
  }, [facilitySummaryData])

  // Fetch patients for a facility when expanded (supports pagination via page param)
  const loadFacilityPatients = useCallback(
    async (facilityId: string, page = 1) => {
      setFacilities((prev) =>
        prev.map((f) =>
          f.facilityId === facilityId ? { ...f, patientsLoading: true } : f
        )
      )

      try {
        const response = await equipApi.dashboard.getPimsFacilityPatients(
          facilityId,
          {
            providerNpi: providerNpi,
            year,
            quarter,
            page,
            pageSize: 200,
          }
        )

        setFacilities((prev) =>
          prev.map((f) => {
            if (f.facilityId !== facilityId) return f
            const existingPatients = page > 1 && f.patients ? f.patients : []
            return {
              ...f,
              patients: [...existingPatients, ...response.patients],
              patientsLoading: false,
              patientsTotalCount: response.totalCount,
              patientsPage: page,
            }
          })
        )
      } catch (error) {
        console.error('Error loading facility patients:', error)
        setFacilities((prev) =>
          prev.map((f) =>
            f.facilityId === facilityId ? { ...f, patientsLoading: false } : f
          )
        )
      }
    },
    [providerNpi, year, quarter]
  )

  // Handle row expansion
  const handleToggleExpanded = useCallback(
    (facilityId: string) => {
      const facility = facilities.find((f) => f.facilityId === facilityId)
      if (facility && !facility.patients && !facility.patientsLoading) {
        loadFacilityPatients(facilityId)
      }
    },
    [facilities, loadFacilityPatients]
  )

  // Create column helper
  const columnHelper = createColumnHelper<FacilityWithPatients>()

  // Facility columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('facilityName', {
        header: ({ column }) => <SortableHeader label="Facility Name" column={column} />,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 transition-transform text-muted-foreground',
                row.getIsExpanded() && 'rotate-90'
              )}
            />
            <span className="text-sm font-medium">{row.original.facilityName}</span>
          </div>
        ),
      }),
      columnHelper.accessor('patientCount', {
        header: ({ column }) => <SortableHeader label="Patient Count" column={column} />,
        cell: ({ row }) => <p className="text-sm">{row.original.patientCount}</p>,
      }),
      columnHelper.accessor('avgPcpPimsScore', {
        header: ({ column }) => <SortableHeader label="Avg PCP PIMs" column={column} />,
        cell: ({ row }) => (
          <p className="text-sm">
            {row.original.avgPcpPimsScore?.toFixed(1) ?? '-'}
          </p>
        ),
      }),
      columnHelper.accessor('avgPsychPimsScore', {
        header: ({ column }) => <SortableHeader label="Avg Psych PIMs" column={column} />,
        cell: ({ row }) => (
          <p className="text-sm">
            {row.original.avgPsychPimsScore?.toFixed(1) ?? '-'}
          </p>
        ),
      }),
    ],
    [columnHelper]
  )

  const table = useReactTable({
    data: facilities,
    columns,
    state: {
      sorting,
      globalFilter,
      expanded,
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowCanExpand: () => true,
  })

  // Track visible width of scroll container so expansion rows can clamp to it.
  // Depends on isLoading: when embedded, the early-return loading state means the
  // ref target doesn't exist on first mount. Re-run when loading completes so the
  // ResizeObserver attaches to the now-rendered scroll container.
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    cleanupRef.current?.()
    const sync = () => el.style.setProperty('--container-w', `${el.clientWidth}px`)
    sync()
    const ro = new ResizeObserver(() => sync())
    ro.observe(el)
    cleanupRef.current = () => ro.disconnect()
    return () => {
      ro.disconnect()
      cleanupRef.current = null
    }
  }, [isLoading])

  // Viewport-filling height for the main table scroll container (skip when embedded)
  useLayoutEffect(() => {
    if (embedded) return
    const el = scrollContainerRef.current
    if (!el) return
    const recalc = () => {
      const top = el.getBoundingClientRect().top
      const maxH = Math.max(200, window.innerHeight - top - 30)
      el.style.maxHeight = `${maxH}px`
    }
    recalc()
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [facilities, embedded])

  // CSV export handler
  const handleExportCSV = useCallback(() => {
    const headers = ['Facility Name', 'Patient Count', 'Avg PCP PIMs', 'Avg Psych PIMs']
    const rows = facilities.map((f) => [
      f.facilityName,
      f.patientCount,
      f.avgPcpPimsScore?.toFixed(1) ?? '-',
      f.avgPsychPimsScore?.toFixed(1) ?? '-',
    ])
    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `pims-facilities-${providerNpi}-${year}-Q${quarter}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [facilities, providerNpi, year, quarter])

  if (isLoading) {
    if (embedded) {
      return <p className="text-sm text-muted-foreground py-4">Loading PIMs data...</p>
    }
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Loading PIMs data...</p>
      </Card>
    )
  }

  const Wrapper = embedded ? 'div' : Card
  const wrapperProps = embedded ? { className: 'space-y-4' } : { className: 'p-6' }

  return (
    <Wrapper {...wrapperProps}>
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">PIMs Facility Summary</h3>
          <div className="flex items-center gap-2">
            {/* Search toggle */}
            {!showSearch ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSearch(true)}
              >
                <Search className="h-4 w-4" />
              </Button>
            ) : (
              <Input
                placeholder="Search facilities..."
                className="w-56"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                onBlur={() => {
                  if (!globalFilter) setShowSearch(false)
                }}
              />
            )}
            {/* CSV export */}
            <Button variant="ghost" size="icon" onClick={handleExportCSV}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className={equipTable.wrapper}>
          <div ref={scrollContainerRef} className="overflow-auto">
          <AnimatedTableWrapper>
            <table className={equipTable.table}>
              <thead className={equipTable.thead}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={equipTable.theadTr}>
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={header.id}
                        className={cn(equipTable.th, headerIdx === 0 && equipTable.stickyTh)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <AnimatedTableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-4 text-muted-foreground"
                    >
                      No facilities found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <Fragment key={row.id}>
                      <AnimatedTableRow
                        index={index}
                        className={`${equipTable.tr} ${equipTable.trAlt(index)} cursor-pointer`}
                        onClick={() => {
                          row.toggleExpanded()
                          handleToggleExpanded(row.original.facilityId)
                        }}
                      >
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td key={cell.id} className={cn(equipTable.td, cellIdx === 0 && (index % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd))}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </AnimatedTableRow>
                      <AnimatePresence initial={false}>
                        {row.getIsExpanded() && (
                          <tr>
                            <td colSpan={columns.length} className="p-0">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  height: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
                                  opacity: { duration: 0.25, delay: 0.08 },
                                }}
                                style={{ clipPath: 'inset(0)' }}
                              >
                                {/* Sticky wrapper: clipPath (not overflow-hidden) on the
                                    motion.div lets sticky reach the scroll container, so the
                                    expansion stays pinned and clamped to the visible viewport
                                    with no lateral bleed. */}
                                <div className="sticky left-0 [overflow:clip]" style={{ width: 'var(--container-w)' }}>
                                  <SubGrid isExpanded={true} depth={1} variant="compact">
                                    <FacilityBreadcrumbTracker
                                      facilityId={row.original.facilityId}
                                      facilityName={row.original.facilityName || 'Facility'}
                                      isExpanded={row.getIsExpanded()}
                                      onCollapse={() => row.toggleExpanded()}
                                    />
                                    <SubGridContent>
                                      {row.original.patientsLoading ? (
                                        <div className="flex items-center gap-2 py-4 px-3">
                                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                          <p className="text-sm text-muted-foreground">Loading patient medication data...</p>
                                        </div>
                                      ) : row.original.patients &&
                                        row.original.patients.length > 0 ? (
                                        <>
                                          <SortablePatientTable
                                            patients={row.original.patients}
                                            onPatientClick={onPatientClick}
                                          />
                                          {row.original.patientsTotalCount != null &&
                                            row.original.patients.length < row.original.patientsTotalCount && (
                                            <div className="flex items-center justify-between px-3 py-2 border-t border-border/50">
                                              <p className="text-xs text-muted-foreground">
                                                Showing {row.original.patients.length} of {row.original.patientsTotalCount} patients
                                              </p>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-xs h-7"
                                                disabled={row.original.patientsLoading}
                                                onClick={() =>
                                                  loadFacilityPatients(
                                                    row.original.facilityId,
                                                    (row.original.patientsPage ?? 1) + 1
                                                  )
                                                }
                                              >
                                                {row.original.patientsLoading ? 'Loading...' : 'Load More'}
                                              </Button>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <p className="text-sm text-muted-foreground py-3 px-3">
                                          No patient data available
                                        </p>
                                      )}
                                    </SubGridContent>
                                  </SubGrid>
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </Fragment>
                  ))
                )}
              </AnimatedTableBody>
            </table>
          </AnimatedTableWrapper>
          </div>
        </div>

        {/* Pagination Controls */}
        {table.getPageCount() > 0 && (
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">Show</p>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="w-fit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">per page</p>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground">
                {table.getRowModel().rows.length > 0
                  ? `${
                      table.getState().pagination.pageIndex *
                        table.getState().pagination.pageSize +
                      1
                    }-${Math.min(
                      (table.getState().pagination.pageIndex + 1) *
                        table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length
                    )} of ${table.getFilteredRowModel().rows.length}`
                  : '0 of 0'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="h-8 w-8"
                >
                  ←
                </Button>
                <span className="text-sm font-medium">
                  {table.getState().pagination.pageIndex + 1}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="h-8 w-8"
                >
                  →
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Wrapper>
  )
}

/* ─── Facility Breadcrumb Tracker ─── */

function FacilityBreadcrumbTracker({
  facilityId,
  facilityName,
  isExpanded,
  onCollapse,
}: {
  facilityId: string
  facilityName: string
  isExpanded: boolean
  onCollapse: () => void
}) {
  const { trail, addLevel, removeLevel } = useBreadcrumbs()
  const headerRef = useRef<HTMLDivElement>(null)
  const breadcrumbId = `facility-${facilityId}`

  // Use a ref to read the latest trail value inside the effect without
  // including trail in the dependency array. Including trail caused the
  // effect to re-run every time any breadcrumb was added/removed, which
  // triggered removeLevel→addLevel cycles on every click and caused
  // DOM re-renders mid-click that swallowed the event.
  const trailRef = useRef(trail)
  trailRef.current = trail

  // Stable ref for onCollapse so the effect never needs onCollapse as a dep.
  const onCollapseRef = useRef(onCollapse)
  onCollapseRef.current = onCollapse

  useEffect(() => {
    if (isExpanded) {
      // Determine depth: count existing levels and use depth = max + 1
      const currentTrail = trailRef.current
      const maxDepth = currentTrail.length > 0 ? Math.max(...currentTrail.map((l) => l.depth)) : 0
      const depth = maxDepth + 1

      addLevel({
        id: breadcrumbId,
        label: facilityName,
        depth,
        onCollapse: () => onCollapseRef.current(),
        scrollTarget: headerRef.current || undefined,
      })
    } else {
      removeLevel(breadcrumbId)
    }

    return () => {
      removeLevel(breadcrumbId)
    }
  }, [isExpanded, facilityId, facilityName, addLevel, removeLevel, breadcrumbId])

  // Render a zero-height sentinel at the top of the expanded facility section
  return <div ref={headerRef} className="h-0 w-full" />
}

/* ─── Sortable Patient Subgrid ─── */

type SortKey = 'patientName' | 'pcpPimsScore' | 'psychPimsScore' | 'acbScore' | 'totalRoutineMeds' | 'antipsychotic' | 'benzodiazepine' | 'opioids' | 'depakoteForMood'

const PATIENT_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'patientName', label: 'Patient Name' },
  { key: 'pcpPimsScore', label: 'PCP PIMs' },
  { key: 'psychPimsScore', label: 'Psych PIMs' },
  { key: 'acbScore', label: 'ACB Score' },
  { key: 'totalRoutineMeds', label: 'Total Routine Meds' },
  { key: 'antipsychotic', label: 'Antipsychotic' },
  { key: 'benzodiazepine', label: 'Benzodiazepine' },
  { key: 'opioids', label: 'Opioids' },
  { key: 'depakoteForMood', label: 'Depakote' },
]

function SortablePatientTable({
  patients,
  onPatientClick,
}: {
  patients: PIMsPatientDetail[]
  onPatientClick: (patient: { patientId: number; defaultTab: string }) => void
}) {
  const [sortKey, setSortKey] = useState<SortKey>('patientName')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...patients].sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      if (typeof aVal === 'string') return aVal.localeCompare(bVal as string) * dir
      if (typeof aVal === 'boolean') return ((aVal ? 1 : 0) - (bVal as number)) * dir
      return ((aVal as number) - (bVal as number)) * dir
    })
  }, [patients, sortKey, sortDir])

  return (
    <div className="border rounded-lg border-ld overflow-x-auto overscroll-x-contain bg-card">
      <table className={equipTable.table}>
        <thead className={equipTable.thead}>
          <tr className={equipTable.theadTr}>
            {PATIENT_COLUMNS.map((col, colIdx) => (
              <th
                key={col.key}
                className={cn(
                  equipTable.th,
                  'cursor-pointer select-none hover:text-primary/80',
                  col.key === 'patientName' && 'min-w-[160px]',
                  colIdx === 0 && equipTable.stickyTh,
                )}
                onClick={() => handleSort(col.key)}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  <ArrowUpDown className={cn(
                    'h-3 w-3',
                    sortKey === col.key ? 'opacity-100' : 'opacity-30',
                  )} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((patient, pIdx) => (
            <motion.tr
              key={patient.patientId}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.15 + Math.min(pIdx * 0.04, 0.8) }}
              className={`${equipTable.trClickable} ${equipTable.trAlt(pIdx)} group`}
              onClick={() =>
                onPatientClick({
                  patientId: patient.patientId,
                  defaultTab: 'pims',
                })
              }
            >
              <td className={cn(equipTable.td, 'font-medium', pIdx % 2 === 1 ? equipTable.stickyTdAlt : equipTable.stickyTd)}>
                <span className="inline-flex items-center gap-1.5">
                  {patient.patientName}
                  {patient.missingMedicationList && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400 cursor-default shrink-0">
                            <AlertTriangle className="h-3 w-3" />
                            No Med List
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[260px] text-xs">
                          This patient's PCP PIMs score is set to 15 and Psych PIMs score is set to 3
                          because their medication list has 1 or fewer entries. The medication list may need to be updated in the EHR.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </span>
              </td>
              <td className={equipTable.td}>{patient.pcpPimsScore?.toFixed(1) ?? '-'}</td>
              <td className={equipTable.td}>{patient.psychPimsScore?.toFixed(1) ?? '-'}</td>
              <td className={equipTable.td}>{patient.acbScore?.toFixed(1) ?? '-'}</td>
              <td className={equipTable.td}>{patient.totalRoutineMeds ?? '-'}</td>
              <td className={equipTable.td}>{patient.antipsychotic ? <Check className="h-5 w-5 text-foreground stroke-[3]" /> : '-'}</td>
              <td className={equipTable.td}>{patient.benzodiazepine ? <Check className="h-5 w-5 text-foreground stroke-[3]" /> : '-'}</td>
              <td className={equipTable.td}>{patient.opioids ? <Check className="h-5 w-5 text-foreground stroke-[3]" /> : '-'}</td>
              <td className={equipTable.td}>
                <span className="flex items-center gap-1">
                  {patient.depakoteForMood ? <Check className="h-5 w-5 text-foreground stroke-[3]" /> : '-'}
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity ml-auto flex-shrink-0" />
                </span>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
